import { FunctionComponent, useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import Icon from "@mdi/react";
import { debounce } from "lodash";
import {
  mdiArrowBottomLeft,
  mdiClose,
  mdiMenuDown,
  mdiMenuUp,
  mdiRefresh,
} from "@mdi/js";
import moment from "moment";
import { WAREHOUSE_TYPE, WarehouseModel } from "../models/warehouse_model";
import { ItemModelV2 } from "../models/item_model";
import {
  ToastType,
  useToast,
} from "../../../context_providers/toast/toast_ctx";
import { fetchGet, toQueryString } from "../../../service/api_client";
import {
  GetStartAndEndDateOfMonth,
  UTCToLocalDate,
} from "../../../utils/date_util";
import LoadingWidget from "../../../context_providers/modal/loader";
import Pagination from "../../../ui/new_components/common/pagination";
import { ACTION, MODULE } from "../../../utils/enums";
import ItemSearchField from "../items/components/ItemSearchField";
import WarehouseMultiSelect from "../warehouse/warehouse_multi_select";
import FilterDropDown from "../../../ui/new_components/common/filter_drop_down";
import { GetWareHouseListApi } from "../inventory_basket/components/warehouse_repo";
import ItemSearchFieldV2 from "../items/item_search_fieldv2";
import { MontlyStockRecordTrackModel } from "../models/inventory_montly_stock_model";
import ModalDialog from "../../../ui/new_components/common/modal";
import FileDropZone from "../../../ui/new_components/common/file_drop_zone";
import { useAuth } from "../../../context_providers/auth/authProvider";

interface InventoryStockTrackerScreenProps {
  records: MontlyStockRecordTrackModel[];
  onClick: (id: string) => void;
  sort_by: string;
  sort_order: number;
  pages: number;
  options: []; // Replace with actual type
  handleSort: (data: { sort: string; sort_order: number }) => void;
  handlePageChange: (page: number) => void;
  // handleStatusFilterChange: (selectedOptions: string[]) => void;
}

interface StockListFilter {
  item_code?: string;
  status?: string[];
  item_name?: string;
  warehouses?: string[];
  warehouse_type?: string[];
  item_type?: string;
  category?: string;
  sub_category?: string;
  search?: string;
  page: number;
  count: number;
  all?: any;
  item_id?: string;
  fromDate?: Date;
  toDate?: Date;
}

const defaultFilter = (): StockListFilter => {
  const now = new Date();
  const { start_date, end_date } = GetStartAndEndDateOfMonth(now);
  // const start_date = new Date(new Date(now).setDate(now.getDate() - 30));
  // const end_date = now;
  return {
    page: 1,
    count: 100,
    all: false,
    fromDate: start_date,
    toDate: end_date,
  };
};

const InventoryStockTrackerScreen: FunctionComponent<
  InventoryStockTrackerScreenProps
> = () => {
  const navigation = useHistory();
  const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/target-inventory/get-inventory-tracker-list`;
  const { showToast } = useToast();
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(50);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [data, setData] = useState<MontlyStockRecordTrackModel[]>([]);
  const tempFilter = localStorage.getItem("invtracker_filter");
  const [filter, setFilter] = useState<StockListFilter>(
    tempFilter ? JSON.parse(tempFilter) : defaultFilter,
  );
  const [filterCollaped, setFilterCollapsed] = useState(true);
  const [isMaker, setIsMaker] = useState<boolean>(false);
  const [isViewer, setIsViewer] = useState<boolean>(false);
  const [selectedType, setSelectedType] = useState<string>("");
  const [items, setItems] = useState<ItemModelV2>();
  const [selectedWarehouses, setSelectedWarehouses] = useState<
    WarehouseModel[]
  >([]);
  const [bulkImport, setBulkImport] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const { user, isAuthorised } = useAuth();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedHandler = useCallback(
    debounce((prop: StockListFilter) => {
      if (prop) fetchData(prop);
    }, 700),
    [],
  );

  console.log("DAte filter:", filter.fromDate, filter.toDate);

  useEffect(() => {
    localStorage.setItem("invtracker_filter", JSON.stringify(filter));
  }, [filter]);

  useEffect(() => {
    const view_permission_result = isAuthorised({
      module: MODULE.master_vendor_checker,
      action: ACTION.READ,
    });
    const maker_permission_result = isAuthorised({
      module: MODULE.material_planning_uploader,
      action: ACTION.CREATE,
    });
    setIsViewer(view_permission_result);
    setIsMaker(maker_permission_result);
    // console.log(" 1122 Checker", checker_permission_result);
    // console.log("1122 Maker", maker_permission_result);
  }, [isAuthorised]);

  useEffect(() => {
    debouncedHandler(filter);
  }, [filter, debouncedHandler]);

  useEffect(() => {
    const loadSelectedWarehouses = async () => {
      if (filter.warehouses && filter.warehouses.length > 0) {
        const res = await GetWareHouseListApi({
          page: 1,
          count: 1000,
          all: true,
        });
        if (res.success) {
          const allWarehouses = res.data.data;
          const hydrated = filter.warehouses
            .map((id) => allWarehouses.find((w: WarehouseModel) => w.id === id))
            .filter(Boolean) as WarehouseModel[];
          setSelectedWarehouses(hydrated);
        }
      }
    };

    loadSelectedWarehouses();
  }, []);

  const fetchData = async (prop: StockListFilter) => {
    setLoading(true);
    try {
      const res = await fetchGet(url + toQueryString(prop));
      console.log("res : ", res);

      if (res.success) {
        setCount(res.data.metadata?.count);
        setData(res.data.data);
        if (res.data.metadata) {
          setTotal(res.data.metadata.total);
        }
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error: any) {
      showToast({ type: ToastType.error, text: error.message });
    } finally {
      setLoading(false);
    }
  };

  const onImport = async (file: File) => {
    setLoading(true);
    const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/target-inventory/import-montly-target-inventory`;
    const form = new FormData();
    form.append("file", file, file.name);

    try {
      const response = await fetch(url, {
        method: "POST",
        body: form,
      });

      const result = await response.json();

      if (!response.ok) {
        // HTTP-level error
        showToast({
          type: ToastType.error,
          text: result.error || result.message || `Error ${response.status}`,
        });
      } else {
        // Status OK: check business logic
        if (result.success) {
          showToast({
            type: ToastType.success,
            text: result.message || "Upload succeeded",
          });
          // Refresh the data using same filter so page updates
          fetchData(filter);
        } else {
          // result.success is false — could be duplicate or other business failures
          // You can inspect result.error or code to see if it's duplicate
          if (
            result.errorCode === "DUPLICATE_UPLOAD" ||
            result.error?.toLowerCase().includes("duplicate")
          ) {
            showToast({
              type: ToastType.error,
              text: "This sheet has already been uploaded. Duplicate uploads are not allowed.",
            });
          } else {
            showToast({
              type: ToastType.error,
              text: result.error || result.message || "Upload failed",
            });
          }
        }
      }
    } catch (err: any) {
      showToast({
        type: ToastType.error,
        text: err.message || "Upload failed.",
      });
    } finally {
      setLoading(false);
    }
  };

  // const onImport = async (file: File) => {
  //   setLoading(true);
  //   const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/target-inventory/import-montly-target-inventory`;
  //   const form = new FormData();
  //   form.append("file", file, file.name);
  //   try {
  //     setLoading(true);

  //     const response = await fetch(url, {
  //       method: "POST",
  //       body: form, // FormData with file upload
  //     });

  //     const result = await response.json();

  //     if (!response.ok) {
  //       // HTTP status not okay (e.g. 400)
  //       // result should contain your `error` from backend
  //       showToast({
  //         type: ToastType.error,
  //         text: result.error || result.message || `Error ${response.status}`,
  //       });
  //     } else {
  //       // status OK, then check business logic success
  //       if (result.success) {
  //         showToast({
  //           type: ToastType.success,
  //           text: result.message || "Upload succeeded",
  //         });
  //         fetchData()
  //       } else {
  //         showToast({
  //           type: ToastType.error,
  //           text: result.error || result.message || "Upload failed",
  //         });
  //       }
  //     }
  //   } catch (err: any) {
  //     showToast({
  //       type: ToastType.error,
  //       text: err.message || "Upload failed.",
  //     });
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const getDeviceInventoryExport = async () => {
    try {
      setLoading(true);

      const searchProp = { ...filter };
      searchProp.all = true;
      const response = await ExportDeviceIntentory(searchProp);

      console.log(response);
      if (response.success) {
        showToast({
          type: ToastType.success,
          text: response.message,
        });
      } else {
        showToast({
          type: ToastType.error,
          text: response.message,
        });
      }
    } catch (error: any) {
      console.error("Export initiation error:", error);

      showToast({ type: ToastType.error, text: error.error });
    } finally {
      setLoading(false);
    }
  };

  const ExportDeviceIntentory = async (props: StockListFilter) => {
    const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/npd/device-inventory-export`;

    const res = await fetchGet(url + toQueryString(props));
    return res;
  };

  const clearLocalStore = () => {
    // Remove from localStorage
    localStorage.removeItem("invtracker_filter");

    // Reset filter state
    const defaultFilterObj = defaultFilter();
    setFilter(defaultFilterObj);

    // Trigger UI updates and fetch
    debouncedHandler(defaultFilterObj);
    fetchData(defaultFilterObj);
  };

  const requiredHeaders = [
    "WAREHOUSE ID",
    "ITM-48900023",
    "ITM-48900024",
    "ITM-48900025",
  ];

  const handleDownloadTemplate = () => {
    const headers = requiredHeaders;

    const csvContent = [headers]
      .map((row) => row.map((cell) => `"${cell}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "material_import.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      {/* <Maintainence></Maintainence> */}
      {showImport && (
        <ModalDialog
          show={showImport}
          onClose={() => setShowImport(false)}
          title="Import POs "
        >
          <div className="text-xs w-96">
            <h2 className="font-semibold">Please note :</h2>
            <p>sheet should have header as</p>
            <p className="font-semibold text-purple-500">
              WAREHOUSE ID, and Item codes..
            </p>
            <p>In the first row of csv.</p>
          </div>
          <div className="h-96 w-96 mt-5">
            {/* <FileDropZone
              accept=".csv"
              onDrop={(files) => {
                parse<any>(files[0], {
                  header: true,
                  skipEmptyLines: true,
                  complete: ({ data: rows, errors, meta }) => {
                    if (rows.length === 0) {
                      showToast({
                        type: ToastType.info,
                        text: "No record found !",
                      });
                      return;
                    }

                    const keys = meta.fields || Object.keys(rows[0]);
                    const missing = requiredHeaders.filter(
                      (h) => !keys?.includes(h)
                    );
                    if (missing.length) {
                      showToast({
                        type: ToastType.error,
                        text: `Required headers missing: [${missing.join(
                          ", "
                        )}]`,
                      });
                      return;
                    }

                    setShowImport(false);
                    onImport(files[0]);
                  },
                });
              }}
            /> */}
            <FileDropZone
              accept=".csv"
              onDrop={(files) => {
                const csvFile = files[0];
                setShowImport(false);
                onImport(csvFile);
              }}
            />
          </div>
        </ModalDialog>
      )}
      <div
        id="page_container"
        className="flex flex-col justify-between h-full p-1"
      >
        <section
          id="top_section"
          className="flex justify-between items-end my-1"
        >
          <h1 className="font-bold text-2xl text-gray-500 flex items-center gap-3">
            Material Planning
            <button
              id="refresh_btn"
              title="Refersh"
              onClick={() => debouncedHandler({ ...filter })}
            >
              <Icon path={mdiRefresh} className="h-6 w-6"></Icon>
            </button>
            <button
              id="clear_filter_btn"
              className="flex gap-1 items-center cursor-pointer rounded p-1 bg-myPrimaryColor hover:scale-105 duration-200 text-sm text-white transform"
              onClick={() => clearLocalStore()}
            >
              Clear Filter
            </button>
          </h1>

          <div className="text-sm flex flex-wrap items-center gap-2 m-3">
            <div className="text-sm flex items-center gap-4">
              <div className="flex items-center gap-1">
                <label htmlFor="fromDate" className="">
                  From
                </label>
                <input
                  type="date"
                  name=""
                  id="fromDate"
                  className="p-1 rounded focus:outline-none"
                  value={
                    filter.fromDate
                      ? moment(filter.fromDate).format("YYYY-MM-DD")
                      : ""
                  }
                  onChange={(e) => {
                    const now = UTCToLocalDate(e.target.value)!;
                    const fromDate = new Date(
                      new Date(now).setHours(0, 0, 0, 0),
                    );
                    setFilter((o) => ({ ...o, fromDate }));
                  }}
                />
              </div>
              <div className="flex items-center gap-1">
                <label htmlFor="toDate" className="">
                  To
                </label>
                <input
                  type="date"
                  name=""
                  id="toDate"
                  className="p-1 rounded focus:outline-none"
                  value={
                    filter.toDate
                      ? moment(filter.toDate).format("YYYY-MM-DD")
                      : ""
                  }
                  onChange={(e) => {
                    const now = UTCToLocalDate(e.target.value)!;
                    const toDate = new Date(
                      new Date(now).setHours(23, 59, 59, 999),
                    );
                    setFilter((o) => ({ ...o, toDate }));
                  }}
                />
              </div>
              {(filter.fromDate || filter.toDate) && (
                <button
                  id="clear_dates_btn"
                  title="Clear dates"
                  onClick={() =>
                    setFilter((o) => ({
                      ...o,
                      fromDate: undefined,
                      toDate: undefined,
                    }))
                  }
                  className="flex gap-1 items-center cursor-pointer rounded p-1 border hover:scale-105 duration-200 transform"
                >
                  <Icon path={mdiClose} size={0.7}></Icon>
                </button>
              )}
            </div>
            {isMaker && (
              <div className="flex flex-wrap text-sm items-center gap-2 ">
                <div
                  id="import_btn"
                  onClick={() => {
                    setShowImport(true);
                  }}
                  className="flex gap-1 items-center cursor-pointer rounded p-1 bg-myPrimaryColor hover:scale-105 duration-200  text-white  transform"
                >
                  <Icon path={mdiArrowBottomLeft} size={0.8} />
                  Import
                </div>
                <label
                  id="download_template_btn"
                  onClick={handleDownloadTemplate}
                  className="rounded-lg border px-2 py-1 hover:bg-gray-800 hover:text-white flex gap-2 items-center"
                >
                  ⭳
                </label>
                {/* <button
                onClick={() => getDeviceInventoryExport()}
                className="rounded-lg border px-2 py-1 hover:bg-gray-800 hover:text-white flex gap-2 items-center"
              >
                <Icon path={mdiArrowTopRight} size="15"></Icon> Export
              </button> */}
              </div>
            )}
            <div className="flex gap-2">
              <h6 className="font-normal">Filters</h6>
              <div
                id="filters_toggle_btn"
                onClick={() => setFilterCollapsed(!filterCollaped)}
                className="flex   items-center justify-center bg-gray-200 p-1 rounded-full hover:bg-gray-800 hover:text-white"
              >
                <Icon
                  path={filterCollaped ? mdiMenuDown : mdiMenuUp}
                  className="w-4 h-4"
                />
              </div>
            </div>
          </div>
        </section>{" "}
        {!filterCollaped && (
          <section className="flex items-start justify-start m-4">
            <div className="grid grid-cols-2 gap-6 w-1/2">
              <div id="item_code_filter" className="relative">
                <label
                  htmlFor="item_code"
                  className="text-md pt-1 font-semibold"
                >
                  Item Code
                </label>
                <ItemSearchField
                  defaulValue={filter.item_code ?? ""}
                  onClear={() => {
                    setFilter((prevData) => ({
                      ...prevData,
                      item_code: "", // Directly updating item_code
                    }));
                  }}
                  onSubmit={(value: string) =>
                    setFilter((prevData) => ({
                      ...prevData,
                      item_code: value, // Directly updating item_code
                    }))
                  }
                />
              </div>
              <div id="item_name_filter" className="relative w-full">
                <label
                  htmlFor="product_name"
                  className="text-md pt-1 font-semibold"
                >
                  Item Name
                </label>
                <span>
                  <ItemSearchFieldV2
                    placeholder="Select vendor"
                    onSelect={(d) => {
                      setFilter((o) => ({
                        ...o,
                        item_id: d?.id ?? undefined,
                        item_name: d?.name ?? undefined,
                      }));
                      setItems(d);
                    }}
                    value={items}
                  />
                  {filter.item_id && (
                    <button
                      id="clear_item_name_btn"
                      title="Clear vendor"
                      onClick={() => {
                        setFilter((o) => ({
                          ...o,
                          item_id: undefined,
                          item_name: undefined,
                        }));
                        setItems(undefined);
                      }}
                      // className="flex gap-1 items-center cursor-pointer rounded p-1 border hover:scale-105 duration-200 transform bg-white"
                      className="absolute right-2 top-1/2 transform -translate-y-1/4 bg-white hover:scale-105 p-1 rounded"
                    >
                      <Icon path={mdiClose} size={0.7}></Icon>
                    </button>
                  )}
                </span>
              </div>
              <div id="warehouse_type_filter" className="flex flex-col gap-1">
                <label className="text-md">Warehouse Type</label>
                <FilterDropDown
                  placeholder="Warehouse Type"
                  onClearAll={() =>
                    setFilter({
                      ...filter,
                      warehouse_type: undefined, // Fixed indentation
                    })
                  }
                  onDelete={(type) => {
                    setFilter({
                      ...filter,
                      warehouse_type: filter?.warehouse_type?.filter(
                        (f: any) => f !== type,
                      ),
                    });
                  }}
                  onSelect={(type) => {
                    setFilter((old) => ({
                      ...old,
                      warehouse_type: [...(old?.warehouse_type ?? []), type],
                    }));
                  }}
                  selectedOptions={filter?.warehouse_type ?? []}
                  options={Object.values(WAREHOUSE_TYPE).map((t) => ({
                    label: t,
                    value: t,
                  }))}
                />
              </div>
              <div id="from_warehouse_filter" className="">
                <label className="text-md pt-1 font-semibold">
                  From Warehouse
                </label>
                <WarehouseMultiSelect
                  onSelect={(selectedWarehouse: WarehouseModel) => {
                    setFilter((prevFilter) => {
                      // Prevent duplicate selections
                      if (
                        prevFilter.warehouses?.some(
                          (w) => w === selectedWarehouse.id,
                        )
                      ) {
                        return prevFilter; // Ignore duplicate entry
                      }

                      return {
                        ...prevFilter,
                        warehouses: [
                          ...(prevFilter?.warehouses || []),
                          selectedWarehouse.id,
                        ],
                      };
                    });

                    setSelectedWarehouses((prevWarehouses) => {
                      // Prevent duplicate selections
                      if (
                        prevWarehouses.some(
                          (v) => v.id === selectedWarehouse.id,
                        )
                      ) {
                        return prevWarehouses; // Ignore duplicate entry
                      }

                      return [...prevWarehouses, selectedWarehouse]; // Append the new vendor
                    });
                  }}
                  onDelete={(id: string) => {
                    setFilter((prevFilter) => ({
                      ...prevFilter,
                      warehouses: (prevFilter?.warehouses || []).filter(
                        (w) => w !== id,
                      ),
                    }));

                    setSelectedWarehouses((prevWarehouses) =>
                      prevWarehouses.filter((v) => v.id !== id),
                    );
                  }}
                  selectedWarehouses={selectedWarehouses ?? []}
                />
              </div>
            </div>
          </section>
        )}
        <div className="my-2 border overflow-auto" style={{ height: "80%" }}>
          <table className="w-full     text-xs divide-y divide-gray-200 relative ">
            <thead className="  text-xs   py-2 sticky -top-1 bg-white z-10">
              <tr className="border ">
                <th className="px-6 py-3 whitespace-nowrap text-left">
                  Warehouse ID
                </th>
                <th className="px-6 py-3 whitespace-nowrap text-left">
                  Warehouse
                </th>
                <th className="px-6 py-3 whitespace-nowrap text-left">
                  Warehouse Type
                </th>
                <th className="px-6 py-3 whitespace-nowrap text-left">
                  Item Code
                </th>
                <th className="px-6 py-3 whitespace-nowrap text-left">
                  Inventory Stock
                </th>
                <th className="px-6 py-3 whitespace-nowrap text-left">
                  Closing Stock
                </th>
                <th className="px-6 py-3 whitespace-nowrap text-left">
                  Installation Stock Requirement
                </th>
                <th className="px-6 py-3 whitespace-nowrap text-left">
                  Net Inventory
                </th>
              </tr>
            </thead>
            <tbody className="">
              {data?.length > 0 ? (
                data.map((data, i) => {
                  return (
                    <tr
                      id={`stock_row_${data?.warehouse_id}_${data?.item_code}`}
                      className="border text-center "
                      key={i}
                    >
                      <td className="px-6 py-3 whitespace-nowrap text-left">
                        {data?.warehouse_id}
                      </td>

                      <td className="px-6 py-3 whitespace-nowrap text-left">
                        {data?.warehouse_name}
                      </td>

                      <td className="px-6 py-3 whitespace-nowrap text-left">
                        {data?.warehouse_type}
                      </td>

                      <td className="px-6 py-3 whitespace-nowrap text-left">
                        {data?.item_code}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-left">
                        {data?.item_name}
                      </td>
                      <td className="px-6 py-3 w-40 truncate text-left">
                        {data?.closing_qty}
                      </td>

                      <td className="px-6 py-3 whitespace-nowrap text-left">
                        {data?.total_target_qty}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-left">
                        {Number(data?.closing_qty ?? 0) -
                          Number(data?.total_target_qty ?? 0)}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={13}>
                    <div className="h-56 text-center p-5 my-20">
                      <h5 className="text-gray-400 text-2xl">No Data found!</h5>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="mt-3">
          <Pagination
            itemCount={count}
            page={filter.page}
            total={total}
            count={filter.count}
            onChange={(page) => setFilter((o) => ({ ...o, page }))}
          />
        </div>
      </div>
      {loading && <LoadingWidget />}
    </>
  );
};

export default InventoryStockTrackerScreen;
